import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Calendar, FileText, Image as ImageIcon } from "lucide-react";

export default function AdminSessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", course_id: "", assigned_group_ids: [], assigned_student_ids: [] });

  const load = useCallback(async () => {
    const [se, c, g, st] = await Promise.all([
      api.get("/sessions"), api.get("/courses"), api.get("/groups"), api.get("/students")
    ]);
    setSessions(se.data);
    setCourses(c.data);
    setGroups(g.data);
    setStudents(st.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", description: "", course_id: courses[0]?.id || "", assigned_group_ids: [], assigned_student_ids: [] });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title, description: s.description || "",
      course_id: s.course_id || "", assigned_group_ids: s.assigned_group_ids || [],
      assigned_student_ids: s.assigned_student_ids || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/sessions/${editing.id}`, form);
        toast.success("Session updated");
      } else {
        await api.post("/sessions", form);
        toast.success("Session created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/sessions/${id}`);
      toast.success("Session deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleGroup = (gid) => {
    setForm((f) => ({
      ...f,
      assigned_group_ids: f.assigned_group_ids.includes(gid)
        ? f.assigned_group_ids.filter((x) => x !== gid)
        : [...f.assigned_group_ids, gid]
    }));
  };

  const toggleStudent = (sid) => {
    setForm((f) => ({
      ...f,
      assigned_student_ids: f.assigned_student_ids.includes(sid)
        ? f.assigned_student_ids.filter((x) => x !== sid)
        : [...f.assigned_student_ids, sid]
    }));
  };

  const getGroupName = (id) => groups.find((g) => g.id === id)?.name || id;
  const getStudentName = (id) => students.find((s) => s.id === id)?.name || id;

  const filteredGroupsForForm = useMemo(() => {
    if (!form.course_id) return groups;
    return groups.filter((g) => g.course_id === form.course_id);
  }, [groups, form.course_id]);

  return (
    <AdminLayout>
      <div data-testid="admin-sessions" className="space-y-6 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Practical Sessions</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">Manage hands-on project sessions</p>
          </div>
          <Button data-testid="create-session-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Plus className="w-4 h-4 mr-1.5" /> New Session
          </Button>
        </div>

        <div className="grid gap-4">
          {sessions.map((s) => (
            <div key={s.id} data-testid={`session-card-${s.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6 studio-card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{s.title}</h3>
                  {s.description && <p className="text-[#2B2B2B]/50 text-sm mt-1">{s.description}</p>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(s.assigned_group_ids || []).map((gid) => (
                      <span key={gid} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#EADBC8] text-[#5C3A2E]">
                        {getGroupName(gid)}
                      </span>
                    ))}
                    {(s.assigned_student_ids || []).map((sid) => (
                      <span key={sid} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#C6A969]/20 text-[#5C3A2E]">
                        {getStudentName(sid)}
                      </span>
                    ))}
                  </div>
                  {/* Content indicators */}
                  <div className="flex items-center gap-3 mt-2">
                    {(s.materials || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#2B2B2B]/40">
                        <FileText className="w-3 h-3" /> {s.materials.length} resource{s.materials.length !== 1 && "s"}
                      </span>
                    )}
                    {(s.images || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#2B2B2B]/40">
                        <ImageIcon className="w-3 h-3" /> {s.images.length} image{s.images.length !== 1 && "s"}
                      </span>
                    )}
                    {s.notes && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#C6A969]">Has notes</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button data-testid={`edit-session-${s.id}`} variant="ghost" size="sm" onClick={() => navigate(`/admin/sessions/${s.id}`)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button data-testid={`delete-session-${s.id}`} variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {sessions.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">No sessions yet. Create your first practical session.</p>
            </div>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">{editing ? "Edit Session" : "New Session"}</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">{editing ? "Update session details" : "Create a new practical session"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
                <Input data-testid="session-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Circle Skirt Project" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description</label>
                <Textarea data-testid="session-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-xl border-[#EADBC8] resize-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                  <SelectTrigger data-testid="session-course-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-2 block">Assign to Groups</label>
                <div className="space-y-2">
                  {filteredGroupsForForm.map((g) => (
                    <label key={g.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#F7F3EF] cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={form.assigned_group_ids.includes(g.id)}
                        onChange={() => toggleGroup(g.id)}
                        className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969]"
                      />
                      <span className="text-sm text-[#2B2B2B]">{g.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-2 block">Assign to Individual Students</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {students.map((st) => (
                    <label key={st.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#F7F3EF] cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={form.assigned_student_ids.includes(st.id)}
                        onChange={() => toggleStudent(st.id)}
                        className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969]"
                      />
                      <span className="text-sm text-[#2B2B2B]">{st.name}</span>
                      <span className="text-xs text-[#2B2B2B]/30">{st.email}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="save-session-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                {editing ? "Save Changes" : "Create Session"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
